import { useQuery, useQueryClient } from "@tanstack/react-query";
import { profileService, TUserProfile } from "services/profile";
import { useCallback, useMemo } from "react";

export const useGetUsersProfileBatch = (userIds: (string | undefined)[]) => {
  // Deduplicate and filter non-empty user IDs
  const validUserIds = useMemo(() => {
    return Array.from(new Set(userIds.filter((id): id is string => Boolean(id && id.trim()))));
  }, [userIds]);

  const queryKey = useMemo(() => ["userProfilesBatch", [...validUserIds].sort().join(",")], [validUserIds]);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery(
    queryKey,
    async () => {
      try {
        if (validUserIds.length === 0) {
          return await profileService.getAllUserProfiles();
        }
        const batchRes = await profileService.getUserProfilesBatch(validUserIds);
        if (!batchRes?.data || batchRes.data.length < validUserIds.length) {
          const allRes = await profileService.getAllUserProfiles();
          return allRes;
        }
        return batchRes;
      } catch (err) {
        console.warn("Fallback to getAllUserProfiles on batch error:", err);
        return profileService.getAllUserProfiles();
      }
    },
    {
      staleTime: 0, // Always consider fresh data
      refetchOnWindowFocus: true,
      refetchOnMount: true
    }
  );

  const profilesList = useMemo(() => {
    const list = data?.data || [];
    if (process.env.NODE_ENV !== "production" && list.length > 0) {
      console.log("[useGetUsersProfileBatch] loaded profiles list:", list);
    }
    return list;
  }, [data]);

  const profilesMapByUserId = useMemo(() => {
    const map: Record<string, TUserProfile> = {};
    profilesList.forEach(profile => {
      if (profile.userId) {
        map[profile.userId.toLowerCase().trim()] = profile;
        map[profile.userId] = profile;
      }
    });
    return map;
  }, [profilesList]);

  const profilesMapByEmail = useMemo(() => {
    const map: Record<string, TUserProfile> = {};
    profilesList.forEach(profile => {
      const email = profile.profileData?.email;
      if (email) {
        map[email.toLowerCase().trim()] = profile;
      }
    });
    return map;
  }, [profilesList]);

  const getProfile = useCallback(
    (userId?: string, email?: string): TUserProfile | undefined => {
      if (userId) {
        const trimmedId = userId.toLowerCase().trim();
        if (profilesMapByUserId[trimmedId]) {
          return profilesMapByUserId[trimmedId];
        }
        if (profilesMapByUserId[userId]) {
          return profilesMapByUserId[userId];
        }
      }
      if (email) {
        const trimmedEmail = email.toLowerCase().trim();
        if (profilesMapByEmail[trimmedEmail]) {
          return profilesMapByEmail[trimmedEmail];
        }
      }
      // Direct search fallback
      if (userId || email) {
        const found = profilesList.find(p => {
          const pUserId = p.userId ? p.userId.toLowerCase().trim() : "";
          const pEmail = p.profileData?.email ? p.profileData.email.toLowerCase().trim() : "";
          const matchUserId = Boolean(userId && pUserId === userId.toLowerCase().trim());
          const matchEmail = Boolean(email && pEmail === email.toLowerCase().trim());
          return matchUserId || matchEmail;
        });
        if (found) return found;
      }
      return undefined;
    },
    [profilesMapByUserId, profilesMapByEmail, profilesList]
  );

  return {
    profilesList,
    profilesMap: profilesMapByUserId,
    profilesMapByUserId,
    profilesMapByEmail,
    getProfile,
    getProfileByUserId: (userId?: string) => getProfile(userId),
    isLoading,
    isFetching,
    isError,
    error,
    refetch
  };
};

export const useInvalidateUsersProfileBatch = () => {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries(["userProfilesBatch"]);
  };
};

export default useGetUsersProfileBatch;
